import React, { useEffect, useState, useCallback, useMemo } from "react";
import { PageProps } from "../models/global";
import "../assets/css/Home.css";
import {Helmet} from "react-helmet";
import { FaCheck, FaEnvelope, FaLinkedin, FaWhatsapp } from "react-icons/fa";
import { hideMenu } from "../utils";
import { TranslationKey, useTranslate } from "../services/translation";
import LazyImage from "../components/LazyImage";
import { getPortfolio, PortfolioData } from "../api/API";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../LanguageContext";
import { formatDateByLang, getYear } from "../services/utils";
import InfoTooltip from "../components/InfoTooltip";
import { ProfileSkeleton, BiographySkeleton, WorkSkeleton, ContactSkeleton } from "../components/SkeletonLoaders";
import ProductList from "../components/ProductList";
import { ProductSkeletonLoader } from "../components/ProductSkeletonLoader";
import { useNavigate } from "react-router-dom";
import { App } from "../environments/environment";

const Home = ({start}: PageProps) => {
  const { translate } = useTranslate();
  const { language } = useLanguage();
  const { startLoading, stopLoading } = useLoading();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState<PortfolioData | null>(null);

  const getLanguagePrefixedLink = (link: string) => {
    return `/${language}${link}`;
  };

  const loadPortfolio = useCallback(async () => {
    startLoading();
    const data = await getPortfolio(App.PORTFOLIO_USER_ID);
    setPortfolio(data);
    stopLoading();
  }, [startLoading, stopLoading])
  
  const sortedExperiences = useMemo(() => {
    return [...(portfolio?.experiences || [])].sort((a, b) => {
      return new Date(b.start_date).getTime() - new Date(a.start_date).getTime();
    });
  }, [portfolio?.experiences]);

  useEffect(()=>{
    start!(0);
    hideMenu();
    loadPortfolio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <div className="home width-100p">
      <Helmet>
          <meta charSet='utf-8' name='description' content={translate('portfolio.gence.home_page.desc')}/>
          <title>{translate("portfolio.gence.home_page.title")}</title>
          <link rel="canonical" href="https://gence.bemanjary.com"/>
      </Helmet>

      {/* Profile Section */}
      {portfolio ? (
        <div className="profile-container width-100p flex">
          <div className="height-300 width-300 radius-50p gradient-bg photo-container flex j-c-center a-i-center relative">
            <LazyImage alt={"profile"} src={portfolio?.profile.avatar_url!}/>
          </div>
          {portfolio?.profile.bio && (
            <div className="grow-1 left-20" dangerouslySetInnerHTML={{__html: translate(portfolio.profile.bio as TranslationKey)}}/>
          )}
        </div>
      ) : (
        <ProfileSkeleton />
      )}

      {/* Biography Section */}
      {portfolio ? (
        <div className="biography">
          <h3 className="white">{translate('common.biography')}</h3>
          <ul className="font-18">
            {portfolio?.skills
              .sort((a, b) => new Date(b.since_date).getTime() - new Date(a.since_date).getTime())
              .map(skill => (
                <li key={skill.title}>
                  <span className="checked">
                    <FaCheck />
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span className="light">
                      {translate(skill.title as any)}{" "}
                      {translate("common.since")}{" "}
                      {getYear(skill.since_date)}
                    </span>
                    {skill.description && <InfoTooltip descriptionKey={skill.description} />}
                  </div>
                </li>
              ))
            }
            {sortedExperiences.map((exp, index) => (
              <li key={index}>
                <span className="checked">
                  <FaCheck />
                </span>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span className="light">
                    {translate(exp.title as any)} {translate("common.at_location")} {exp.company}{" "}
                    {!exp.is_current ? (
                      <>
                        {formatDateByLang(language, exp.start_date)}{" "}
                        {translate("common.to_date")}{" "}
                        {formatDateByLang(language, exp.end_date)}
                      </>
                    ) : (
                      <>
                        {translate("common.since")}{" "}
                        {formatDateByLang(language, exp.start_date)}
                      </>
                    )}
                  </span>
                  {exp.description && <InfoTooltip descriptionKey={exp.description} />}
                </div>
              </li>
            ))}
            {portfolio?.formations.map(educ=><li key={educ.id}><span className="checked">
              <FaCheck/>
            </span><div style={{ display: 'flex', alignItems: 'center' }}><span className="light">{translate(educ.title as any)} {translate("common.in_date")} {getYear(educ.end_date)}</span>{educ.description && <InfoTooltip descriptionKey={educ.description} />}</div></li>)}
          </ul>
        </div>
      ) : (
        <BiographySkeleton />
      )}

      {/* Work Section */}
      {portfolio ? (
        <div className="work">
          <h3 className="white">{translate('common.what_i_do')}</h3>
          <div className="width-100p flex work-container">
            <p className="light font-18 work-desc">{translate(portfolio?.profile.what_i_do as TranslationKey)}</p>
            <div className="work-images-section">
              <div className="work-image-card">
                <LazyImage
                  alt="Development"
                  src="https://upload-api.bemanjary.com/api/v1/upload/file?object_name=public1776978828181.png"
                />
              </div>
              <div className="work-image-card">
                <LazyImage
                  alt="Design"
                  src="https://upload-api.bemanjary.com/api/v1/upload/file?object_name=public1777084706165.png"
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <WorkSkeleton />
      )}

      {/* Contact Section */}
      {portfolio ? (
        <div className="contact">
          <h3 className="white">{translate('common.my_contacts')}</h3>
          <p className="flex a-i-center font-18">
            <span className="contact-icon">
            <FaEnvelope/>
            </span>
          <span className="light">{portfolio?.profile.email}</span>
          </p>
          <p className="flex a-i-center font-18">
            <span className="contact-icon">
            <FaWhatsapp/>
            </span>
            <span className="light">{portfolio?.profile.whatsapp}</span>
          </p>
          <p className="flex a-i-center font-18">
            <span className="contact-icon">
            <FaLinkedin/>
            </span>
            <span className="light"><a className="light link-none" title="LinkedIn" href={portfolio?.profile.linkedin_url!} target="_blank" rel="noopener noreferrer">{portfolio?.profile.linkedin_url}</a></span>
          </p>
        </div>
      ) : (
        <ContactSkeleton />
      )}

      <div className="current-activity">
        <h3 className="white">{translate('common.my_recent_works')}</h3>
        {portfolio && portfolio.products && portfolio.products.length > 0 ? (
          <ProductList
            products={portfolio.products}
            limit={6}
            showViewMore={portfolio.products.length > 6}
            onViewMore={() => {
              // Naviguer vers la page produits
              navigate(getLanguagePrefixedLink('/products'));
            }}
          />
        ) : portfolio ? (
          <div style={{ padding: '40px 20px', textAlign: 'center' }}>
            <p className="light">{translate("common.no_project_available")}</p>
          </div>
        ) : (
          <ProductSkeletonLoader count={6} />
        )}
      </div>
  </div>;
};

export default Home;
